/*
 * WorkAndBuhin.vue の作業選択、部品種別推定、手入力・純正部品選択時のタブ遷移を切り出した処理。
 */
import { BuhinCategory } from "@/model/Buhin";
import { isJunseiBuhinCode } from "@/components/work-buhin/WorkBuhinMatch";
import { INSPECT_TYPE } from "@/model/InspectType";

export interface WorkBuhinTabDecision {
  buhinShubetsuCd: string;
  isActiveExt: boolean;
  isActiveJunsei: boolean;
}

export interface WorkSelectActionState {
  shouldContinue: boolean;
  shouldClearManualBuhinFilter: boolean;
  shouldResetSelectionState: boolean;
  firstBuhinCd: any;
  nRetryScroll: number;
  initialIsDiscount: boolean;
  hasBuhinData: boolean;
  shouldSelectWork: boolean;
  shouldSnapshotWorkBeforeChange: boolean;
  isManualWork: boolean;
  activeWorkIndex: number;
  activeSeibiId: any;
  shouldSetActiveSeibiNm: boolean;
  activeSeibiNm: string;
  isDiscount: boolean;
}

export interface WorkSelectApplyEffectState {
  shouldContinue: boolean;
  shouldClearManualBuhinFilter: boolean;
  shouldResetSelectionState: boolean;
  firstBuhinCd: any;
  nRetryScroll: number;
  initialIsDiscount: boolean;
  hasBuhinData: boolean;
  shouldSelectWork: boolean;
  shouldSnapshotWorkBeforeChange: boolean;
  isManualWork: boolean;
  activeWorkIndex: number;
  activeSeibiId: any;
  shouldSetActiveSeibiNm: boolean;
  activeSeibiNm: string;
  isDiscount: boolean;
}

export interface WorkSelectTabActionState {
  type: "osusume" | "junseiTab" | "junseiApi" | "extreme" | "buhinShubetsu";
  osusumeBuhinList: any[];
  shouldSetOsusumeBuhinList: boolean;
  shouldReturnAfterAction: boolean;
  shouldScrollToBuhin: boolean;
  buhinShubetsuCd: string;
  buhinBunruiList: any;
  sknNisseirenCd: any;
}

export interface WorkSelectTabEffectState {
  shouldSelectOsusumeTab: boolean;
  shouldSelectJunseiTab: boolean;
  shouldRequestJunseiBuhin: boolean;
  shouldSelectExtremeTab: boolean;
  shouldSelectBuhinShubetsu: boolean;
}

export interface WorkSelectTabApplyEffectState extends WorkSelectTabEffectState {
  shouldSetOsusumeBuhinList: boolean;
  osusumeBuhinList: any[];
  sknNisseirenCd: any;
  buhinShubetsuCd: string;
  buhinBunruiList: any;
  shouldReturnAfterAction: boolean;
  shouldScrollToBuhin: boolean;
}

/*
 * findBuhinShubetsuCdById を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function findBuhinShubetsuCdById(
  buhinCategory: BuhinCategory[],
  buhinShubetsuId: any
): string {
  const category = buhinCategory.find(
    (cate: BuhinCategory) => cate.buhinShubetsuId == buhinShubetsuId
  );
  return category && category.buhinShubetsuCd ? category.buhinShubetsuCd : "";
}

/*
 * findBuhinShubetsuCdByBuhinCd を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function findBuhinShubetsuCdByBuhinCd(
  buhinCategory: BuhinCategory[],
  buhinCd: any
): string {
  let buhinShubetsuCd = "";
  // 既存実装の探索範囲を維持するため、末尾要素は探索対象に含めない。
  // tslint:disable-next-line:prefer-for-of
  for (let i = 0; i < buhinCategory.length; i++) {
    const shubetsu: BuhinCategory = buhinCategory[i];
    for (let j = 0; j < shubetsu.items.length - 1; j++) {
      const bunrui = shubetsu.items[j];
      for (let k = 0; k < bunrui.items.length - 1; k++) {
        const buhin = bunrui.items[k];
        if (buhin.buhinCd == buhinCd) {
          buhinShubetsuCd = shubetsu.buhinShubetsuCd || "";
          break;
        }
      }
    }
  }
  return buhinShubetsuCd;
}

/*
 * resolveWorkBuhinTabDecision を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function resolveWorkBuhinTabDecision(params: {
  work: any;
  buhinCategory: BuhinCategory[];
  isDialog: boolean;
  hasExtremeMaster: boolean;
  isUseJunseiBuhin: boolean;
}): WorkBuhinTabDecision {
  const work = params.work;
  let buhinShubetsuCd = "";
  let isActiveExt = false;
  let isActiveJunsei = false;

  if (work.buhinShubetsuCd) {
    buhinShubetsuCd = work.buhinShubetsuCd;
  } else if (
    work.buhinList &&
    work.buhinList.length > 0 &&
    work.buhinList[0].buhinShubetsuId
  ) {
    buhinShubetsuCd = findBuhinShubetsuCdById(
      params.buhinCategory,
      work.buhinList[0].buhinShubetsuId
    );
  } else if (params.hasExtremeMaster) {
    isActiveExt = true;
  } else if (
    !params.isDialog &&
    work.buhinList &&
    work.buhinList.length > 0 &&
    work.buhinList[0].buhinCd
  ) {
    buhinShubetsuCd = findBuhinShubetsuCdByBuhinCd(
      params.buhinCategory,
      work.buhinList[0].buhinCd
    );
  } else if (
    work.buhinList &&
    work.buhinList.length == 0 &&
    work.manualBuhinList &&
    work.manualBuhinList.length > 0 &&
    work.manualBuhinList[0].buhinShubetsuId
  ) {
    buhinShubetsuCd = findBuhinShubetsuCdById(
      params.buhinCategory,
      work.manualBuhinList[0].buhinShubetsuId
    );
  }

  if (work.buhinList && work.buhinList.length == 0 && params.isUseJunseiBuhin) {
    if (
      work.manualBuhinList &&
      work.manualBuhinList.length > 0 &&
      work.manualBuhinList.findIndex(
        (buhin: any) => buhin.buhinCd != null && isJunseiBuhinCode(buhin.buhinCd)
      ) !== -1
    ) {
      isActiveJunsei = true;
    } else if (work.sknNisseirenCd) {
      isActiveJunsei = true;
    }
  }

  return {
    buhinShubetsuCd,
    isActiveExt,
    isActiveJunsei
  };
}

/*
 * shouldClearManualBuhinFilter を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function shouldClearManualBuhinFilter(params: {
  currentIndex: number;
  currentIsManual: boolean;
  nextIndex: number;
  nextIsManual: boolean;
  manualBuhinList: any;
}): boolean {
  return !(
    params.nextIndex == params.currentIndex &&
    params.currentIsManual == params.nextIsManual
  ) && !!params.manualBuhinList;
}

/*
 * shouldSelectWork を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function shouldSelectWork(params: {
  forceSelect: boolean;
  currentIndex: number;
  currentIsManual: boolean;
  nextIndex: number;
  nextIsManual: boolean;
}): boolean {
  return params.forceSelect || !(
    params.nextIndex == params.currentIndex &&
    params.currentIsManual == params.nextIsManual
  );
}

/*
 * buildWorkSelectActionState を WorkAndBuhin.vue から分離し、
 * 作業選択時の入口初期化と選択 state 更新条件を固定するため。
 */
export function buildWorkSelectActionState(params: {
  work: any;
  index: number;
  isManual: boolean;
  forceSelect: boolean;
  activeWorkIndex: number;
  isManualWork: boolean;
  isDialog: boolean;
  listBuhinDataLength: number;
}): WorkSelectActionState {
  if (params.work == null) {
    return {
      shouldContinue: false,
      shouldClearManualBuhinFilter: false,
      shouldResetSelectionState: false,
      firstBuhinCd: null,
      nRetryScroll: 0,
      initialIsDiscount: false,
      hasBuhinData: false,
      shouldSelectWork: false,
      shouldSnapshotWorkBeforeChange: false,
      isManualWork: params.isManualWork,
      activeWorkIndex: params.activeWorkIndex,
      activeSeibiId: null,
      shouldSetActiveSeibiNm: false,
      activeSeibiNm: "",
      isDiscount: false
    };
  }

  const shouldExecuteSelect = shouldSelectWork({
    forceSelect: params.forceSelect,
    currentIndex: params.activeWorkIndex,
    currentIsManual: params.isManualWork,
    nextIndex: params.index,
    nextIsManual: params.isManual
  });

  return {
    shouldContinue: true,
    shouldClearManualBuhinFilter: shouldClearManualBuhinFilter({
      currentIndex: params.activeWorkIndex,
      currentIsManual: params.isManualWork,
      nextIndex: params.index,
      nextIsManual: params.isManual,
      manualBuhinList: params.work.manualBuhinList
    }),
    shouldResetSelectionState: true,
    firstBuhinCd: null,
    nRetryScroll: 0,
    initialIsDiscount: false,
    hasBuhinData: params.listBuhinDataLength > 0,
    shouldSelectWork: shouldExecuteSelect,
    shouldSnapshotWorkBeforeChange: shouldExecuteSelect && !params.isDialog,
    isManualWork: params.isManual,
    activeWorkIndex: params.index,
    activeSeibiId: params.work.seibiDetailId,
    shouldSetActiveSeibiNm: params.isManual,
    activeSeibiNm: params.work.seibiNm,
    isDiscount: !!params.work.isDiscount
  };
}

/*
 * buildWorkSelectApplyEffectState を WorkAndBuhin.vue から分離し、
 * 作業選択時に component が反映する初期化、active 作業更新、snapshot、割引状態の副作用条件を固定するため。
 */
export function buildWorkSelectApplyEffectState(
  actionState: WorkSelectActionState
): WorkSelectApplyEffectState {
  return {
    shouldContinue: actionState.shouldContinue,
    shouldClearManualBuhinFilter: actionState.shouldClearManualBuhinFilter,
    shouldResetSelectionState: actionState.shouldResetSelectionState,
    firstBuhinCd: actionState.firstBuhinCd,
    nRetryScroll: actionState.nRetryScroll,
    initialIsDiscount: actionState.initialIsDiscount,
    hasBuhinData: actionState.hasBuhinData,
    shouldSelectWork: actionState.shouldSelectWork,
    shouldSnapshotWorkBeforeChange: actionState.shouldSnapshotWorkBeforeChange,
    isManualWork: actionState.isManualWork,
    activeWorkIndex: actionState.activeWorkIndex,
    activeSeibiId: actionState.activeSeibiId,
    shouldSetActiveSeibiNm: actionState.shouldSetActiveSeibiNm,
    activeSeibiNm: actionState.activeSeibiNm,
    isDiscount: actionState.isDiscount
  };
}

/*
 * isRecommendWork を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function isRecommendWork(work: any): boolean {
  return !!(
    work &&
    work.osusumeBuhinList &&
    work.osusumeBuhinList.length > 0
  );
}

/*
 * 部品未登録の手入力おすすめ整備は、非表示のおすすめタブではなく手入力タブを開く。
 */
export function isEmptyManualRecommendWork(work: any): boolean {
  return !!work && work.sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL &&
    !work.isExtreme &&
    [work.osusumeBuhinList, work.buhinList, work.manualBuhinList].every(
      list => !list || list.length === 0
    );
}

export function shouldOpenOsusumeTab(work: any): boolean {
  return isRecommendWork(work) || !!(
    work && work.sknRecommendInspectCategoryId && !isEmptyManualRecommendWork(work)
  );
}

/*
 * buildWorkSelectTabActionState を WorkAndBuhin.vue から分離し、
 * 作業選択後に実行するタブ/API action の選択条件を固定するため。
 */
export function buildWorkSelectTabActionState(params: {
  work: any;
  tabDecision: WorkBuhinTabDecision;
  forceSelect: boolean;
}): WorkSelectTabActionState {
  if (params.work.osusumeBuhinList && params.work.osusumeBuhinList.length > 0) {
    return {
      type: "osusume",
      osusumeBuhinList: params.work.osusumeBuhinList,
      shouldSetOsusumeBuhinList: true,
      shouldReturnAfterAction: true,
      shouldScrollToBuhin: false,
      buhinShubetsuCd: "",
      buhinBunruiList: null,
      sknNisseirenCd: null
    };
  }
  if (shouldOpenOsusumeTab(params.work)) {
    return {
      type: "osusume",
      osusumeBuhinList: [],
      shouldSetOsusumeBuhinList: false,
      shouldReturnAfterAction: true,
      shouldScrollToBuhin: false,
      buhinShubetsuCd: "",
      buhinBunruiList: null,
      sknNisseirenCd: null
    };
  }
  const shouldClearOsusumeBuhinList = (
    params.work.osusumeBuhinList == null ||
    params.work.osusumeBuhinList.length == 0
  );
  const commonState = {
    osusumeBuhinList: [],
    shouldSetOsusumeBuhinList: shouldClearOsusumeBuhinList,
    shouldReturnAfterAction: false,
    shouldScrollToBuhin: true,
    buhinShubetsuCd: "",
    buhinBunruiList: null,
    sknNisseirenCd: null
  };

  if (params.tabDecision.isActiveJunsei) {
    return {
      ...commonState,
      type: params.forceSelect ? "junseiTab" : "junseiApi",
      sknNisseirenCd: params.work.sknNisseirenCd
    };
  }
  if (params.tabDecision.isActiveExt) {
    return {
      ...commonState,
      type: "extreme"
    };
  }
  return {
    ...commonState,
    type: "buhinShubetsu",
    buhinShubetsuCd: params.tabDecision.buhinShubetsuCd,
    buhinBunruiList: params.work.buhinBunruiList
  };
}

/*
 * buildWorkSelectTabEffectState を WorkAndBuhin.vue から分離し、
 * 作業選択後の tab action type と component 側で実行する副作用の対応を固定するため。
 */
export function buildWorkSelectTabEffectState(
  tabActionState: WorkSelectTabActionState
): WorkSelectTabEffectState {
  return {
    shouldSelectOsusumeTab: tabActionState.type === "osusume",
    shouldSelectJunseiTab: tabActionState.type === "junseiTab",
    shouldRequestJunseiBuhin: tabActionState.type === "junseiApi",
    shouldSelectExtremeTab: tabActionState.type === "extreme",
    shouldSelectBuhinShubetsu: tabActionState.type === "buhinShubetsu"
  };
}

/*
 * buildWorkSelectTabApplyEffectState を WorkAndBuhin.vue から分離し、
 * 作業選択後の tab/API 副作用、return、scroll 条件と引数を固定するため。
 */
export function buildWorkSelectTabApplyEffectState(
  tabActionState: WorkSelectTabActionState
): WorkSelectTabApplyEffectState {
  return {
    ...buildWorkSelectTabEffectState(tabActionState),
    shouldSetOsusumeBuhinList: tabActionState.shouldSetOsusumeBuhinList,
    osusumeBuhinList: tabActionState.osusumeBuhinList,
    sknNisseirenCd: tabActionState.sknNisseirenCd,
    buhinShubetsuCd: tabActionState.buhinShubetsuCd,
    buhinBunruiList: tabActionState.buhinBunruiList,
    shouldReturnAfterAction: tabActionState.shouldReturnAfterAction,
    shouldScrollToBuhin: tabActionState.shouldScrollToBuhin
  };
}

/*
 * isRecommendInspectType を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function isRecommendInspectType(sknInspectType: any): boolean {
  return (
    sknInspectType == INSPECT_TYPE.RECOMMEND ||
    sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL
  );
}

/*
 * createSingleRecommendWork を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function createSingleRecommendWork(data: any): any {
  return {
    seibiNm: data.sknRecommendInspecSeibiNm,
    seibiDetailId: null,
    seibiId: null,
    buhinSelected: data.buhinSelected,
    kousuu: data.kousuu,
    manualBuhinList: Array.isArray(data.manualBuhinList)
      ? data.manualBuhinList
      : [],
    buhinList: Array.isArray(data.osusumeBuhinList)
      ? data.osusumeBuhinList
      : [],
    sknRecommendInspectCategoryId: null,
    kouchinZn: data.kouchinZn
  };
}

/*
 * shouldUseSingleRecommendWork を WorkAndBuhin.vue から分離し、
 * 作業選択とタブ初期化 を親側置換や単体テストで固定するため。
 */
export function shouldUseSingleRecommendWork(data: any, editSeibi: boolean): boolean {
  return !!(editSeibi || (!data.osusumeId && !data.sknInspectSeibiDetailId));
}
